import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AlertController,
  LoadingController,
  ModalController,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { DictionaryService } from 'src/app/dictionary/dictionary.service';
import { HeaderModel } from 'src/app/_component/header/header.model';
import { globalHelper } from 'src/app/_helpers/global';
import { UserService } from 'src/app/_services';
import { HeaderService } from 'src/app/_services/header.service';
import { ThisPlatform } from 'src/app/_services/platform.service';
import { Company, CompanyBasic } from '../company/company.model';
import { CompanyService } from '../company/company.service';
import { CategoryModalPage } from './category-modal/category-modal.page';
import { Product } from './_model/product.model';
import { CartService } from './_services/cart.service';
import { ProductService } from './_services/product.service';

@Component({
  selector: 'app-market',
  templateUrl: './market.page.html',
  styleUrls: ['./market.page.scss'],
})
export class MarketPage implements OnInit {
  cart = [];
  productsFilter: Product[] = [];
  searchValue = '';
  cartItemCount: BehaviorSubject<number>;
  groupIdClicked = 0;
  providerId: number;
  provider: CompanyBasic;

  addProductPermission: boolean = false;
  editCategoryPermission: boolean = false;

  isError: boolean = false;
  errorStatus: number;

  headerInit: HeaderModel = {
    title: 'Market Place',
    rightButtons: [
      {
        iconUrl: './assets/icons/basket.svg',
        onClick: () => this.openCart(),
        rolePermission: [''],
        fill: 'clear',
        counter: this.cartServ.getCartItemCount(),
      },
    ],
    mobileSegmentsChangeFunc: (event) =>
      this.onGroupClick(Number(event.detail.value)),
    mobileSegments: [],
  };

  constructor(
    private cartServ: CartService,
    private modalCtrl: ModalController,
    public dictionaryServ: DictionaryService,
    public productServ: ProductService,
    private route: ActivatedRoute,
    private location: Location,
    public companyServ: CompanyService,
    public userService: UserService,
    private router: Router,
    public loadingCtrl: LoadingController,
    public alertController: AlertController,
    public translateService: TranslateService,
    public p: ThisPlatform,
    private headerServ: HeaderService
  ) {}

  async ngOnInit() {
    this.companyServ.companyBasic = new CompanyBasic();
    this.dictionaryServ.dictionaryMarketGroups = null;

    this.route.paramMap.subscribe(async (paramMap) => {
      if (!paramMap.has('providerId')) {
        return this.location.back();
      }

      this.providerId = Number(paramMap.get('providerId'));

      this.addProductPermission = await this.userService.permissionEditCompanyAsync(
        this.providerId,
        ['market.product.add'],
        ['market.edit.only-my-company']
      );

      this.editCategoryPermission = await this.userService.permissionEditCompanyAsync(
        this.providerId,
        ['market.category.add'],
        ['market.edit.only-my-company']
      );

      this.companyServ.GetOne<Company>(this.providerId).subscribe(
        (providerCompany) => {
          return (this.provider = providerCompany);
        },
        (error) => {
          this.isError = true;
          this.errorStatus = error.status;
        }
      );

      this.productServ
        .getProductByProvider(this.providerId)
        .subscribe((p: Product[]) => {
          this.productServ.products = p;
          this.productsFilter = p;
        });
    });

    await this.dictionaryServ.refreshDictionaryMarketGroup(
      this.providerId,
      true
    );

    this.cart = this.cartServ.getCart();
    this.cartItemCount = this.cartServ.getCartItemCount();

    this.mobileSegmentsInit();
  }
  ionViewWillEnter() {
    this.headerServ.setHeaderInit(this.headerInit);
  }

  mobileSegmentsInit() {
    this.headerInit.mobileSegments = [
      {
        name: 'ALL',
        permissionKeys: [''],
        value: '0',
      },
    ];

    if (this.dictionaryServ.dictionaryMarketGroups) {
      this.dictionaryServ.dictionaryMarketGroups.dictionaryValues.forEach(
        (c) => {
          this.headerInit.mobileSegments.push({
            name: c.key,
            value: c.value.toString(),
            permissionKeys: [''],
            translated: true,
          });
        }
      );
    }
  }

  onGroupClick(id: number) {
    this.searchValue = '';
    this.groupIdClicked = id;
    if (id === 0) {
      this.productsFilter = this.productServ.products;
    } else {
      this.productsFilter = this.productServ.products.filter(
        (a) => a.category === id
      );
    }
  }

  async openCart() {
    this.router.navigateByUrl('/app/market/cart/' + this.providerId);
  }

  async openCategory() {
    const modal = await this.modalCtrl.create({
      component: CategoryModalPage,
      cssClass: 'add-edit-modal add-edit-modal--small',
      componentProps: {
        idDictionary: this.dictionaryServ.dictionaryMarketGroups.id,
      },
    });
    await modal.present();
    await modal.onDidDismiss();
    this.dictionaryServ.refreshDictionaryMarketGroup(this.providerId, true);
  }

  async openEditCategory(idItem: number) {
    const modal = await this.modalCtrl.create({
      component: CategoryModalPage,
      cssClass: 'add-edit-modal add-edit-modal--small',
      componentProps: {
        idDictionary: this.dictionaryServ.dictionaryMarketGroups.id,
        idItem: idItem,
      },
    });
    await modal.present();
    await modal.onDidDismiss();
    this.dictionaryServ.refreshDictionaryMarketGroup(this.providerId, true);
  }

  async Delete(idItem: number) {
    let confirm = await globalHelper.Alert(
      this.alertController,
      this.translateService
    );

    if (confirm) {
      this.loadingCtrl
        .create({
          keyboardClose: true,
          message: this.translateService.instant('DELETING'),
        })
        .then((loadingEl) => {
          loadingEl.present();
          this.dictionaryServ
            .deleteDictionaryValue(
              this.dictionaryServ.dictionaryMarketGroups.id,
              idItem
            )
            .subscribe((res) => {
              this.dictionaryServ.refreshDictionaryMarketGroup(
                this.providerId,
                true
              );
              loadingEl.dismiss();
            });
        });
      this.onGroupClick(0);
    }
  }

  onChangeSearch(value: string) {
    this.productsFilter = this.productServ.products.filter(
      (a) =>
        a.name.toLowerCase().includes(value.toLowerCase()) &&
        a.companyProvider.id === this.providerId &&
        (this.groupIdClicked === 0 || a.category === this.groupIdClicked)
    );
  }

  onEditProductClick(id: number): void {
    this.router.navigateByUrl(
      `/app/market/provider/product/${this.providerId}/${id}`
    );
  }
}
